/**
 * `gamification-core` — projection of local annotations to GameEvents.
 *
 * READING SIGNAL (streak): bible-strong has no `activity` feed or explicit
 * "chapter read" record that syncs with a timestamp. Therefore the
 * streak is DERIVED as a PROXY for the annotation activity that ALREADY
 * exists locally: the UNION of notes, highlights (verse-level and group-level),
 * and bookmarks. Every local day with at least one such annotation counts as an
 * active reading day (the engine collapses same-day events by streak line).
 * It is NON-invasive: it adds no tables and does not touch the server or sync
 * rules. Pure and deterministic: the same records → the same `PlayerState`.
 */
import { BSO_EVENTS, bsoGamificationConfig } from "./config";
import { applyEvents, initialState } from "./engine";
import { dayKeyUTC } from "./streaks";
import type { GameEvent, PlayerState } from "./types";

/** Reading-annotation class. */
export type ReadingKind = "note" | "highlight" | "bookmark" | "planDay";

/**
 * Minimal annotation record (projection of the local tables). `createdAt`
 * is the row's ISO `created_at`; `null`/invalid is discarded.
 */
export interface ReadingRecord {
  kind: ReadingKind;
  createdAt: string | null | undefined;
  /**
   * Record origin. `"legacy-import"` marks data migrated from the OLD
   * Bible Strong app and is EXCLUDED from streaks and XP.
   *
   * Reason: legacy **does not store dates** (its `highlight` table has no
   * time column; it ordered by `rowid`). Migration must stamp
   * `created_at` with the migration instant, and counting that as activity
   * would inject a FALSE SPIKE: hundreds of highlights "created" on the same day,
   * awarding XP and badges the user never earned here. The data is preserved
   * intact; what is not fabricated is the history.
   *
   * The emitter infers it from the row's `origin_id` (`LEGACY_IMPORT_ORIGIN`).
   */
  source?: "user" | "legacy-import";
}

const EVENT_BY_KIND: Record<ReadingKind, string> = {
  note: BSO_EVENTS.noteCreated,
  highlight: BSO_EVENTS.highlightCreated,
  bookmark: BSO_EVENTS.bookmarkCreated,
  planDay: BSO_EVENTS.planDayCompleted,
};

/** Epoch ms of the ISO `created_at`; `null` if missing or unparseable. */
function timestampOf(record: Pick<ReadingRecord, "createdAt">): number | null {
  if (!record.createdAt) {
    return null;
  }
  const t = Date.parse(record.createdAt);
  return Number.isNaN(t) ? null : t;
}

/**
 * Translates local annotations into gamification events in ascending
 * chronological order (as the streak reducer requires). Rows without dates are ignored.
 */
export function deriveGameEvents(records: readonly ReadingRecord[]): GameEvent[] {
  const events: GameEvent[] = [];
  for (const record of records) {
    // What was migrated from legacy has no real date: it cannot generate a streak or XP.
    if (record.source === "legacy-import") {
      continue;
    }
    const at = timestampOf(record);
    if (at === null) {
      continue;
    }
    events.push({ type: EVENT_BY_KIND[record.kind], at, day: dayKeyUTC(at) });
  }
  return events.toSorted((a, b) => a.at - b.at);
}

/**
 * Game state derived from local annotations. Pure: the same records
 * (and the same protections) → the same state. Returns XP/level/reading
 * streak/badges/quests for the UI.
 *
 * `freezes` are streak protections the player ALREADY has. The parameter
 * is optional with a default of 0 on purpose: **the gamification engine
 * does not know ads exist**. Today the rewarded flow supplies them
 * (`growth/domain.ts::adFreezes`), but tomorrow it could be anything else and
 * this file would not know.
 */
export function deriveGameState(records: readonly ReadingRecord[], freezes = 0): PlayerState {
  const events = deriveGameEvents(records);
  return applyEvents(initialState(freezes), events, bsoGamificationConfig).state;
}
